import type {
  EventsSortColumn,
  EventsSortDir,
  FilterState,
  InteractionSessionCohort,
  OpenRawEventsOptions,
} from "../types.js";
import { filterRangeToApiParam, normalizeFilterDateTime } from "./filterDates.js";
import { appendPassthroughUrlParams } from "./urlPassthrough.js";

export interface RawEventsUrlState {
  filters: FilterState;
  eventName?: string;
  propertiesContains?: string;
  propertiesContains2?: string;
  clientErrorBucket?: "" | "warning" | "error";
  interactionSessionId?: string;
  interactionSessionCohort?: InteractionSessionCohort | "";
  eventsOffset?: number;
  eventsPageSize?: number;
  showTemperatureEvents?: boolean;
  showUptimePulse?: boolean;
  eventsHideAttract?: boolean;
  eventsDedupeTapMissed?: boolean;
  tempCelsiusMin?: number;
  tempCelsiusMaxExclusive?: number;
  eventsSort?: EventsSortColumn;
  eventsSortDir?: EventsSortDir;
}

/** Build a shareable Raw Events tab URL (same params as App URL sync). */
export function buildRawEventsUrl(state: RawEventsUrlState): string {
  const params = new URLSearchParams();
  params.set("tab", "events");
  params.set("timeframe", state.filters.timeframe);

  if (state.filters.venueId && state.filters.venueId !== "all") {
    params.set("venue_id", state.filters.venueId);
  }
  if (state.filters.deviceId && state.filters.deviceId !== "all") {
    params.set("device_id", state.filters.deviceId);
  }
  if (state.filters.gameUrl && state.filters.gameUrl !== "all") {
    params.set("game_url", state.filters.gameUrl);
  }
  if (state.filters.timeframe === "custom") {
    if (state.filters.from) params.set("from", filterRangeToApiParam(state.filters.from));
    if (state.filters.to) params.set("to", filterRangeToApiParam(state.filters.to));
  }

  const eventName = state.eventName ?? "all";
  if (eventName !== "all") params.set("event_name", eventName);

  const props = (state.propertiesContains ?? "").trim();
  if (props) params.set("events_props", props.slice(0, 500));
  const props2 = (state.propertiesContains2 ?? "").trim();
  if (props2) params.set("events_props_2", props2.slice(0, 500));

  if (
    eventName === "client_error" &&
    (state.clientErrorBucket === "warning" || state.clientErrorBucket === "error")
  ) {
    params.set("events_cc", state.clientErrorBucket);
  }

  const isid = (state.interactionSessionId ?? "").trim();
  if (isid) params.set("events_isid", isid.slice(0, 128));
  if (state.interactionSessionCohort) params.set("events_iscohort", state.interactionSessionCohort);

  const offset = state.eventsOffset ?? 0;
  if (offset > 0) params.set("events_offset", String(offset));
  const pageSize = state.eventsPageSize ?? 50;
  if (pageSize !== 50) params.set("events_limit", String(pageSize));

  if (state.showTemperatureEvents) params.set("events_inc_temp", "1");
  if (state.showUptimePulse) params.set("events_inc_pulse", "1");
  if (state.eventsHideAttract === false) params.set("events_hide_attract", "0");
  if (state.eventsDedupeTapMissed === false) params.set("events_dedupe_tap_missed", "0");
  if (state.tempCelsiusMin != null) params.set("events_temp_min", String(state.tempCelsiusMin));
  if (state.tempCelsiusMaxExclusive != null) {
    params.set("events_temp_max_excl", String(state.tempCelsiusMaxExclusive));
  }

  const sort = state.eventsSort ?? "client_timestamp";
  const sortDir = state.eventsSortDir ?? "desc";
  if (sort !== "client_timestamp") params.set("events_sort", sort);
  if (sortDir !== "desc") params.set("events_sort_dir", sortDir);

  appendPassthroughUrlParams(params);

  return `${window.location.pathname}?${params.toString()}`;
}

/** Same as {@link buildRawEventsUrl}, applying chart drill-down options to the current filters. */
export function buildRawEventsUrlFromOptions(
  filters: FilterState,
  opts: OpenRawEventsOptions,
  extras?: Partial<Omit<RawEventsUrlState, "filters" | "eventName">>,
): string {
  let nextFilters: FilterState = { ...filters };
  if (opts.gameUrl !== undefined) {
    nextFilters = {
      ...nextFilters,
      gameUrl: opts.gameUrl === "all" ? "all" : opts.gameUrl,
    };
  }
  if (opts.venueId !== undefined) {
    nextFilters = {
      ...nextFilters,
      venueId: opts.venueId === "all" ? "all" : opts.venueId,
      deviceId: "all",
    };
  }
  if (opts.deviceId !== undefined) {
    nextFilters = {
      ...nextFilters,
      deviceId: opts.deviceId === "all" ? "all" : opts.deviceId,
    };
  }
  if (opts.drillDownFrom && opts.drillDownTo) {
    nextFilters = {
      ...nextFilters,
      timeframe: "custom",
      from: normalizeFilterDateTime(opts.drillDownFrom, nextFilters.from, "from"),
      to: normalizeFilterDateTime(opts.drillDownTo, nextFilters.to, "to"),
    };
  }
  return buildRawEventsUrl({
    filters: nextFilters,
    eventName: opts.eventName,
    propertiesContains: opts.propertiesContains,
    propertiesContains2: opts.propertiesContains2,
    clientErrorBucket: opts.clientErrorBucket ?? "",
    interactionSessionId: opts.interactionSessionId,
    interactionSessionCohort: opts.interactionSessionCohort ?? "",
    showTemperatureEvents: opts.showTemperatureEvents,
    tempCelsiusMin: opts.tempCelsiusMin,
    tempCelsiusMaxExclusive: opts.tempCelsiusMaxExclusive,
    ...extras,
  });
}
